import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REDIRECT_URL = encodeURIComponent(typeof window !== 'undefined' ? window.location.origin : '');
const OAUTH_BASE_URL = process.env.REACT_APP_OAUTH_URL || "https://auth.emergentagent.com";
const AUTH_URL = `${OAUTH_BASE_URL}/?redirect=${REDIRECT_URL}`;

// Official Google "G" mark
const GoogleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const { checkAuth } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requirePasswordReset, setRequirePasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Mobile keyboards (iOS/Android autocomplete) frequently append a trailing
    // space after autofill — trim defensively so a correct credential isn't
    // silently rejected as wrong.
    const cleanEmail = (formData.email || "").trim();
    const cleanPassword = (formData.password || "").trim();

    // If user typed a username (no @), route to the admin login endpoint
    const isUsername = !cleanEmail.includes("@");
    const endpoint = isUsername ? `${API}/auth/admin/login` : `${API}/auth/login`;
    const payload = isUsername
      ? { username: cleanEmail, password: cleanPassword }
      : { email: cleanEmail, password: cleanPassword };

    try {
      const response = await axios.post(endpoint, payload, { withCredentials: true });
      
      if (response.data.require_password_reset) {
        setRequirePasswordReset(true);
        toast.warning("Password reset required. Please enter a new password.");
        return;
      }
      
      toast.success("Login successful!");
      await checkAuth();
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        new_password: newPassword
      }, { withCredentials: true });
      
      toast.success("Password changed successfully!");
      await checkAuth();
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Password change failed");
    } finally {
      setLoading(false);
    }
  };

  if (requirePasswordReset) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2.5 mb-6">
              <Logo variant="icon" size={32} />
              <span className="text-lg font-semibold tracking-tight text-ink">Golf Eclectic</span>
            </div>
            <p className="ge-eyebrow mb-2">Account security</p>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em] text-ink leading-[1.1]">Set a new password</h1>
            <p className="text-ink-muted mt-3">Your password was reset by an admin. Choose a new one to continue.</p>
          </div>

          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-ink-soft" />
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-ink-soft hover:text-ink-muted"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-ink-soft mt-1">
                    At least 8 characters, 1 uppercase, 1 number
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-ink-soft" />
                    <Input
                      id="confirmNewPassword"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-ink hover:bg-ink-hover"
                  disabled={loading}
                >
                  {loading ? "Changing Password..." : "Change Password"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header — modern wordmark, no cartoon trophy */}
        <div className="text-center mb-8">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2.5 mb-6 hover:opacity-80 transition-opacity"
            data-testid="brand-home-link"
          >
            <Logo variant="icon" size={32} />
            <span className="text-lg font-semibold tracking-tight text-ink">Golf Eclectic</span>
          </button>
          <p className="ge-eyebrow mb-2">Welcome back</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em] text-ink leading-[1.1]">
            Sign in to your <span className="text-accent">eclectic</span>.
          </h1>
        </div>

        <Card>
          <CardContent className="pt-6">
            {/* Google sign-in — front and centre, single click */}
            <button
              type="button"
              onClick={() => (window.location.href = AUTH_URL)}
              data-testid="google-signin-btn"
              className="w-full inline-flex items-center justify-center gap-2.5 bg-surface hover:bg-app border border-line hover:border-line-strong rounded-xl py-3 text-[14.5px] font-medium text-ink transition-all"
            >
              <GoogleIcon />
              <span>Continue with Google</span>
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-line"></div>
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-ink-soft">Or with email</span>
              <div className="flex-1 h-px bg-line"></div>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email or admin username */}
              <div>
                <Label htmlFor="email">Email or username</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-ink-soft" />
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    placeholder="you@example.com or admin username"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-ink-soft" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-ink-soft hover:text-ink-muted"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="login-submit-btn"
                className="w-full bg-ink hover:bg-ink-hover py-6 text-[14.5px] font-semibold"
                disabled={loading}
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>

              {/* Forgot Password Link */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-[12.5px] text-ink-muted hover:text-ink underline-offset-2 hover:underline"
                >
                  Forgot your password?
                </button>
              </div>
            </form>

            {/* Sign Up Link */}
            <div className="mt-6 pt-5 border-t border-line text-center">
              <p className="text-[13px] text-ink-muted">
                Don't have an account?{" "}
                <button
                  onClick={() => navigate("/register")}
                  className="text-accent hover:text-accent-deep font-semibold"
                  data-testid="goto-register-link"
                >
                  Create one
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}